import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  ChevronLeft,
  ChevronRight,
  MessageSquare,
} from 'lucide-react';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useTranslation } from 'react-i18next';
import { Button } from '../../ui';
import { useAnalytics } from '../../../hooks/useAnalytics';
import { CompactMapView } from '../../analytics/CompactMapView';
import { buildFarmGrouping, approximatePolygonAreaM2 } from '../../../utils/areaOverlayUtils';
import './UnifiedModuleBlocks.css';

const SAVE_DELAY_MS = 700;
const SAVED_DISPLAY_MS = 2000;
const DEFAULT_COVERAGE_THRESHOLD = 2;
const isEnglishLocale = (locale) => String(locale || '').toLowerCase().startsWith('en');

const formatTimestamp = (value) => {
  if (!value) {
    return null;
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  return date.toLocaleString();
};

const getUserLabel = (user, fallback) => {
  if (!user) {
    return fallback;
  }
  if (user.name) {
    return user.name;
  }
  const fullName = [user.firstName, user.lastName].filter(Boolean).join(' ');
  if (fullName) {
    return fullName;
  }
  return user.email || fallback;
};

const toNumber = (value) => {
  if (value == null) {
    return null;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const formatNumber = (value, decimals = 2) => {
  const numeric = toNumber(value);
  if (numeric == null) {
    return '-';
  }
  return numeric.toFixed(decimals);
};

const getChartPeriodLabel = (chart) => {
  if (!chart) {
    return '';
  }
  if (chart.period) {
    return chart.period;
  }
  const data = Array.isArray(chart.data) ? chart.data : [];
  if (!data.length) {
    return '';
  }
  const firstLabel = data[0]?.label;
  const lastLabel = data[data.length - 1]?.label;
  if (firstLabel && lastLabel) {
    return firstLabel === lastLabel ? String(firstLabel) : `${firstLabel} - ${lastLabel}`;
  }
  return '';
};

const getStationType = (station) => {
  const raw = station?.type || station?.stationType || station?.sensorType;
  if (!raw) {
    return null;
  }
  const normalized = String(raw).toLowerCase().replace(/\s+/g, '_');
  if (normalized.includes('retro')) {
    return 'retro_modulo';
  }
  if (normalized.includes('campo') || normalized.includes('field')) {
    return 'campo_aperto';
  }
  return normalized;
};

const getStationCoordinates = (station) => {
  if (Array.isArray(station?.coordinates) && station.coordinates.length >= 2) {
    return [station.coordinates[0], station.coordinates[1]];
  }
  if (station?.locationLat != null && station?.locationLon != null) {
    return [station.locationLat, station.locationLon];
  }
  if (station?.lat != null && station?.lon != null) {
    return [station.lat, station.lon];
  }
  if (station?.latitude != null && station?.longitude != null) {
    return [station.latitude, station.longitude];
  }
  return null;
};

const boundsToPolygon = (bounds) => {
  if (!bounds) {
    return null;
  }
  return [
    [bounds.minLat, bounds.minLon],
    [bounds.minLat, bounds.maxLon],
    [bounds.maxLat, bounds.maxLon],
    [bounds.maxLat, bounds.minLon],
  ];
};

const normalizePolygon = (geometry) => {
  if (!geometry) {
    return null;
  }
  if (Array.isArray(geometry)) {
    return geometry;
  }
  if (geometry.type === 'Polygon' && Array.isArray(geometry.coordinates)) {
    const ring = geometry.coordinates[0] || [];
    return ring.map(point => [point[1], point[0]]);
  }
  return null;
};

const isPointInPolygon = (point, polygon) => {
  if (!point || !Array.isArray(polygon) || polygon.length < 3) {
    return false;
  }
  const [lat, lon] = point;
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const [latI, lonI] = polygon[i];
    const [latJ, lonJ] = polygon[j];
    const intersects = (lonI > lon) !== (lonJ > lon)
      && lat < ((latJ - latI) * (lon - lonI)) / (lonJ - lonI) + latI;
    if (intersects) {
      inside = !inside;
    }
  }
  return inside;
};

const resolveZoneId = (zone, index) => zone?.id || zone?.zoneId || zone?.name || `zone-${index + 1}`;

const resolveZoneName = (zone, index, locale) => {
  const fallback = isEnglishLocale(locale) ? `Zone ${index + 1}` : `Zona ${index + 1}`;
  if (!zone) {
    return fallback;
  }
  if (isEnglishLocale(locale)) {
    return zone?.nameEn || zone?.areaNameEn || zone?.labelEn || zone?.name
      || zone?.areaName || zone?.label || zone?.id || fallback;
  }
  return zone?.name || zone?.areaName || zone?.label || zone?.id || fallback;
};

const resolveZonePolygon = (zone) => {
  if (!zone) {
    return null;
  }
  const geometryPolygon = normalizePolygon(zone.geometry);
  if (geometryPolygon) {
    return geometryPolygon;
  }
  if (Array.isArray(zone.polygon)) {
    return zone.polygon;
  }
  if (zone.bounds) {
    return boundsToPolygon(zone.bounds);
  }
  return null;
};

const resolveZoneAreaHa = (zone, polygon) => {
  if (!zone) {
    return 0;
  }
  const directArea = toNumber(zone.areaHa) ?? toNumber(zone.area);
  if (directArea != null) {
    return directArea;
  }
  if (polygon && polygon.length >= 3) {
    return approximatePolygonAreaM2(polygon) / 10000;
  }
  return 0;
};

const computeZoneCounts = (zone, zoneId, stations) => {
  if (!zone) {
    return { campo: 0, retro: 0, total: 0 };
  }
  const campo = toNumber(zone.campoApertoCount);
  const retro = toNumber(zone.retroModuloCount);
  const total = toNumber(zone.totalCount);
  if (campo != null || retro != null || total != null) {
    const campoValue = campo || 0;
    const retroValue = retro || 0;
    const totalValue = total != null ? total : campoValue + retroValue;
    return { campo: campoValue, retro: retroValue, total: totalValue };
  }

  const hasZoneStations = Array.isArray(zone.stations) && zone.stations.length > 0;
  const zoneStations = hasZoneStations ? zone.stations : stations;
  if (!Array.isArray(zoneStations) || zoneStations.length === 0) {
    return { campo: 0, retro: 0, total: 0 };
  }

  const zonePolygon = resolveZonePolygon(zone);
  const zoneSectionIds = new Set(
    (Array.isArray(zone.sectionIds) ? zone.sectionIds : [])
      .map(id => (id != null ? String(id) : null))
      .filter(Boolean)
  );

  let campoCount = 0;
  let retroCount = 0;
  zoneStations.forEach((station) => {
    if (!station) {
      return;
    }
    if (!hasZoneStations) {
      const stationZoneId = station.zoneId != null ? String(station.zoneId) : null;
      const stationSectionId = station.sectionId != null ? String(station.sectionId) : null;
      const matchesZoneId = stationZoneId && zoneId && stationZoneId === String(zoneId);
      let matchesPolygon = false;
      if (!matchesZoneId && zonePolygon) {
        const coords = getStationCoordinates(station);
        if (coords) {
          matchesPolygon = isPointInPolygon(coords, zonePolygon);
        }
      }
      const matchesSection = !matchesZoneId && !matchesPolygon
        ? (stationSectionId && zoneSectionIds.has(stationSectionId))
        : false;
      if (!matchesZoneId && !matchesPolygon && !matchesSection) {
        return;
      }
    }
    const type = getStationType(station);
    if (type === 'retro_modulo') {
      retroCount += 1;
    } else if (type === 'campo_aperto') {
      campoCount += 1;
    }
  });

  return {
    campo: campoCount,
    retro: retroCount,
    total: campoCount + retroCount,
  };
};

const buildCoverageRows = ({ zones, stations, threshold, totalLabel, showTotalRow, locale }) => {
  const rows = zones.map((zone, index) => {
    const zoneId = resolveZoneId(zone, index);
    const zoneName = resolveZoneName(zone, index, locale);
    const polygon = resolveZonePolygon(zone);
    const areaHa = resolveZoneAreaHa(zone, polygon);
    const { campo, retro, total } = computeZoneCounts(zone, zoneId, stations);
    const ratio = areaHa > 0 ? total / areaHa : 0;
    return {
      zone: zoneName,
      area: formatNumber(areaHa),
      retroStations: retro,
      campoStations: campo,
      totalStations: total,
      stationsPerHa: {
        value: formatNumber(ratio),
        meetsTarget: ratio >= threshold,
      },
    };
  });

  if (!showTotalRow) {
    return rows;
  }

  const totalArea = zones.reduce((sum, zone) => {
    const polygon = resolveZonePolygon(zone);
    const area = resolveZoneAreaHa(zone, polygon);
    return sum + (Number.isFinite(area) ? area : 0);
  }, 0);
  const totals = rows.reduce(
    (acc, row) => ({
      retro: acc.retro + (toNumber(row.retroStations) || 0),
      campo: acc.campo + (toNumber(row.campoStations) || 0),
      total: acc.total + (toNumber(row.totalStations) || 0),
    }),
    { retro: 0, campo: 0, total: 0 }
  );
  const totalRatio = totalArea > 0 ? totals.total / totalArea : 0;
  rows.push({
    zone: totalLabel,
    area: formatNumber(totalArea),
    retroStations: totals.retro,
    campoStations: totals.campo,
    totalStations: totals.total,
    stationsPerHa: {
      value: formatNumber(totalRatio),
      meetsTarget: totalRatio >= threshold,
    },
    isTotal: true,
  });
  return rows;
};

const resolveCoverageInputs = (selectedFarm, fallbackCoverage) => {
  if (selectedFarm && Array.isArray(selectedFarm.stations) && selectedFarm.stations.length > 0) {
    const grouping = buildFarmGrouping({
      farm: selectedFarm,
      sections: selectedFarm.sections || [],
      stations: selectedFarm.stations || [],
    });
    let zones = grouping.sections || [];
    if (grouping.groupingLevel === 'quadrant' && Array.isArray(grouping.quadrants) && grouping.quadrants.length > 0) {
      zones = grouping.quadrants;
    } else if (grouping.groupingLevel === 'zone' && Array.isArray(grouping.zones) && grouping.zones.length > 0) {
      zones = grouping.zones;
    }
    return {
      zones,
      stations: selectedFarm.stations || [],
    };
  }
  return {
    zones: Array.isArray(fallbackCoverage?.zones) ? fallbackCoverage.zones : [],
    stations: Array.isArray(fallbackCoverage?.stations) ? fallbackCoverage.stations : [],
  };
};

export function UnifiedModuleBlocks({
  blocks = [],
  moduleId,
  onUpdateBlocks,
  currentUser,
}) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const { selectedFarm } = useAnalytics();
  const [saveState, setSaveState] = useState({});
  const saveTimersRef = useRef({});
  const savedTimersRef = useRef({});
  const userLabel = useMemo(
    () => getUserLabel(currentUser, t('reports.workspace.fallback.user')),
    [currentUser, t]
  );
  const coverageTables = useMemo(() => {
    const tableMap = new Map();
    blocks.forEach(block => {
      if (block.type !== 'table' || block.tableData?.source !== 'stationCoverage') {
        return;
      }
      const threshold = toNumber(block.tableData?.coverageThreshold)
        ?? toNumber(block.tableData?.coverage?.coverageThreshold)
        ?? DEFAULT_COVERAGE_THRESHOLD;
      const { zones, stations } = resolveCoverageInputs(selectedFarm, block.tableData?.coverage);
      const totalLabel = block.tableData?.totalLabelKey
        ? t(block.tableData.totalLabelKey)
        : t('reports.workspace.blocks.coverage.totalLabel');
      const rows = buildCoverageRows({
        zones,
        stations,
        threshold,
        totalLabel,
        showTotalRow: block.tableData?.showTotalRow !== false,
        locale,
      });
      const columns = (block.tableData?.columns || []).map(column => {
        if (column.key === 'stationsPerHa') {
          return { ...column, target: threshold };
        }
        return column;
      });
      tableMap.set(block.id, {
        ...block.tableData,
        editable: false,
        editableHeaders: false,
        columns,
        rows,
      });
    });
    return tableMap;
  }, [blocks, selectedFarm, t, locale]);

  const { sensorSections, standaloneBlocks } = useMemo(() => {
    const sectionMap = new Map();
    const standalone = [];
    blocks.forEach(block => {
      if (block.sectionType !== 'sensor' || !block.sectionId) {
        standalone.push(block);
        return;
      }
      const existing = sectionMap.get(block.sectionId);
      if (existing) {
        existing.blocks.push(block);
        return;
      }
      sectionMap.set(block.sectionId, {
        id: block.sectionId,
        order: Number.isFinite(block.sectionOrder) ? block.sectionOrder : 0,
        sensorName: block.sensorName || block.sectionTitle || '',
        sensorUnit: block.sensorUnit || '',
        sensorPeriod: block.sensorPeriod || '',
        blocks: [block],
      });
    });
    const sections = Array.from(sectionMap.values()).sort((a, b) => a.order - b.order);
    sections.forEach(section => {
      section.blocks.sort((a, b) => (a.blockOrder || 0) - (b.blockOrder || 0));
    });
    return { sensorSections: sections, standaloneBlocks: standalone };
  }, [blocks]);

  useEffect(() => {
    return () => {
      Object.values(saveTimersRef.current).forEach(timer => clearTimeout(timer));
      Object.values(savedTimersRef.current).forEach(timer => clearTimeout(timer));
      saveTimersRef.current = {};
      savedTimersRef.current = {};
    };
  }, []);

  const updateBlock = (blockId, updater) => {
    if (!onUpdateBlocks) {
      return;
    }
    onUpdateBlocks(moduleId, prevBlocks =>
      prevBlocks.map(block => {
        if (block.id !== blockId) {
          return block;
        }
        const updates = typeof updater === 'function' ? updater(block) : updater;
        return { ...block, ...updates };
      })
    );
  };

  const scheduleSave = (blockId) => {
    if (saveTimersRef.current[blockId]) {
      clearTimeout(saveTimersRef.current[blockId]);
    }
    setSaveState(prev => ({ ...prev, [blockId]: 'saving' }));
    saveTimersRef.current[blockId] = setTimeout(() => {
      updateBlock(blockId, {
        lastEditedAt: new Date().toISOString(),
        lastEditedBy: userLabel,
      });
      setSaveState(prev => ({ ...prev, [blockId]: 'saved' }));
      if (savedTimersRef.current[blockId]) {
        clearTimeout(savedTimersRef.current[blockId]);
      }
      savedTimersRef.current[blockId] = setTimeout(() => {
        setSaveState(prev => {
          const next = { ...prev };
          delete next[blockId];
          return next;
        });
      }, SAVED_DISPLAY_MS);
    }, SAVE_DELAY_MS);
  };

  const handleImmediateSave = (blockId) => {
    if (saveTimersRef.current[blockId]) {
      clearTimeout(saveTimersRef.current[blockId]);
    }
    updateBlock(blockId, {
      lastEditedAt: new Date().toISOString(),
      lastEditedBy: userLabel,
    });
    setSaveState(prev => ({ ...prev, [blockId]: 'saved' }));
    if (savedTimersRef.current[blockId]) {
      clearTimeout(savedTimersRef.current[blockId]);
    }
    savedTimersRef.current[blockId] = setTimeout(() => {
      setSaveState(prev => {
        const next = { ...prev };
        delete next[blockId];
        return next;
      });
    }, SAVED_DISPLAY_MS);
  };

  const handleApproveToggle = (block) => {
    const nextApproved = block.status !== 'approved';
    updateBlock(block.id, {
      status: nextApproved ? 'approved' : 'draft',
      approvedBy: nextApproved ? userLabel : null,
      approvedAt: nextApproved ? new Date().toISOString() : null,
    });
  };

  const handleCommentSubmit = (block) => {
    const message = block.commentDraft?.trim();
    if (!message) {
      return;
    }
    const nextHistory = [
      ...(block.commentHistory || []),
      {
        id: `${block.id}-comment-${Date.now()}`,
        text: message,
        createdAt: new Date().toISOString(),
        status: 'submitted',
      },
    ];
    updateBlock(block.id, {
      commentDraft: '',
      commentHistory: nextHistory,
    });
  };

  const getBlockLabel = (block, fallbackKey) => {
    if (block.bodyLabelKey) {
      return t(block.bodyLabelKey);
    }
    if (block.bodyLabel) {
      return block.bodyLabel;
    }
    return t(fallbackKey);
  };

  const resolveBlockTitle = (block, titleOverride) => {
    if (titleOverride !== undefined) {
      return titleOverride;
    }
    if (block.title) {
      return block.title;
    }
    if (block.type === 'text' && block.bodyLabelKey) {
      return t(block.bodyLabelKey);
    }
    return '';
  };

  const resolveColumnLabel = (column) => {
    if (column.label) {
      return column.label;
    }
    if (column.labelKey) {
      return t(column.labelKey, { defaultValue: column.key });
    }
    return column.key;
  };

  const normalizeTagOptions = (options) =>
    (options || []).map(option => {
      if (typeof option === 'string') {
        return { value: option, label: option };
      }
      const value = option.value || option.label || '';
      return {
        value,
        label: option.label,
        labelKey: option.labelKey,
        fallback: option.fallback,
      };
    });

  const resolveTagLabel = (option) => {
    if (option.labelKey) {
      return t(option.labelKey, { defaultValue: option.label || option.fallback || option.value });
    }
    return option.label || option.fallback || option.value;
  };

  const updateTableData = (blockId, updater) => {
    updateBlock(blockId, current => {
      const currentTable = current.tableData || {};
      const nextTable = typeof updater === 'function' ? updater(currentTable) : updater;
      return { tableData: nextTable };
    });
  };

  const renderMetadata = (block) => {
    if (!Array.isArray(block.metadata) || block.metadata.length === 0) {
      return null;
    }
    return (
      <div className="unified-module__block-meta">
        {block.metadata.map((item, index) => (
          <span key={`${block.id}-meta-${index}`} className="unified-module__block-meta-item">
            <span className="unified-module__block-meta-label">
              {item.labelKey ? t(item.labelKey) : item.label}
            </span>
            {item.value ? <span>{item.value}</span> : null}
          </span>
        ))}
      </div>
    );
  };

  const renderChart = (block) => {
    if (block.chart?.variant === 'map') {
      if (!selectedFarm) {
        return (
          <div className="unified-module__empty">
            {t('reports.workspace.blocks.emptyChart')}
          </div>
        );
      }
      const legendVariant = block.chart?.legendVariant;
      const legendTitle = block.chart?.legendTitleKey
        ? t(block.chart.legendTitleKey)
        : block.chart?.legendTitle;
      const mapAriaLabel = block.chart?.ariaLabelKey
        ? t(block.chart.ariaLabelKey)
        : block.chart?.ariaLabel;
      const showLegend = block.chart?.showLegend !== false;
      return (
        <div className="unified-module__map">
          <CompactMapView
            sections={selectedFarm?.sections || []}
            stations={selectedFarm?.stations || []}
            farm={selectedFarm}
            selectedSections={(selectedFarm?.sections || []).map(section => section.id)}
            selectAllSections
            interactive={false}
            showSidebar={false}
            showLegend={showLegend}
            legendVariant={legendVariant}
            legendTitle={legendTitle}
            alwaysShowMarkers={block.chart?.alwaysShowMarkers}
            showMarkerTooltips={block.chart?.showMarkerTooltips}
            ariaLabel={mapAriaLabel}
          />
        </div>
      );
    }

    const data = block.chart?.data || [];
    if (!data.length) {
      return (
        <div className="unified-module__empty">
          {t('reports.workspace.blocks.emptyChart')}
        </div>
      );
    }
    return (
      <div className="unified-module__chart">
        <ResponsiveContainer width="100%" height={240}>
          <LineChart data={data}>
            <CartesianGrid strokeDasharray="3 3" stroke="#EAEAEA" />
            <XAxis dataKey={block.chart?.xKey || 'label'} tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} />
            <Tooltip />
            <Line
              type="monotone"
              dataKey={block.chart?.yKey || 'value'}
              stroke="#4A5A3A"
              strokeWidth={2}
              dot={{ r: 2 }}
              activeDot={{ r: 4 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    );
  };

  const renderTable = (block) => {
    const table = coverageTables.get(block.id) || block.tableData;
    if (!table || !Array.isArray(table.columns) || !Array.isArray(table.rows)) {
      return (
        <div className="unified-module__empty">
          {t('reports.workspace.blocks.emptyTable')}
        </div>
      );
    }
    const handleHeaderChange = (columnIndex, value) => {
      updateTableData(block.id, currentTable => {
        const nextColumns = [...(currentTable.columns || [])];
        const currentColumn = nextColumns[columnIndex] || {};
        nextColumns[columnIndex] = { ...currentColumn, label: value };
        return { ...currentTable, columns: nextColumns };
      });
      scheduleSave(block.id);
    };

    const handleCellChange = (rowIndex, columnKey, value) => {
      updateTableData(block.id, currentTable => {
        const nextRows = [...(currentTable.rows || [])];
        const currentRow = nextRows[rowIndex] || {};
        nextRows[rowIndex] = { ...currentRow, [columnKey]: value };
        return { ...currentTable, rows: nextRows };
      });
      scheduleSave(block.id);
    };

    const handleTagToggle = (rowIndex, columnKey, optionValue) => {
      updateTableData(block.id, currentTable => {
        const nextRows = [...(currentTable.rows || [])];
        const currentRow = nextRows[rowIndex] || {};
        const currentTags = Array.isArray(currentRow[columnKey])
          ? currentRow[columnKey]
          : [];
        const nextTags = currentTags.includes(optionValue)
          ? currentTags.filter(tag => tag !== optionValue)
          : [...currentTags, optionValue];
        nextRows[rowIndex] = { ...currentRow, [columnKey]: nextTags };
        return { ...currentTable, rows: nextRows };
      });
      scheduleSave(block.id);
    };

    return (
      <div className="unified-module__table-wrapper">
        <table className="unified-module__table">
          <thead>
            <tr>
              {table.columns.map((column, columnIndex) => {
                const isHeaderEditable = typeof column.editableHeader === 'boolean'
                  ? column.editableHeader
                  : table.editableHeaders;
                const label = resolveColumnLabel(column);
                return (
                  <th key={`${block.id}-${column.key}`} scope="col">
                    {isHeaderEditable ? (
                      <input
                        className="unified-module__table-input unified-module__table-input--header"
                        value={label}
                        onChange={(event) => handleHeaderChange(columnIndex, event.target.value)}
                        onBlur={() => handleImmediateSave(block.id)}
                        aria-label={label}
                      />
                    ) : (
                      label
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {table.rows.map((row, rowIndex) => (
              <tr
                key={`${block.id}-row-${rowIndex}`}
                className={row.isTotal ? 'unified-module__table-row--total' : ''}
              >
                {table.columns.map(column => {
                  const cellValue = row[column.key];
                  const isRowHeader = column.isRowHeader;
                  const isEditable = typeof column.editable === 'boolean'
                    ? column.editable
                    : table.editable;
                  const CellTag = isRowHeader ? 'th' : 'td';
                  const cellScope = isRowHeader ? 'row' : undefined;

                  if (isRowHeader) {
                    return (
                      <CellTag key={`${block.id}-${column.key}-${rowIndex}`} scope={cellScope}>
                        {cellValue}
                      </CellTag>
                    );
                  }

                  if (column.type === 'tags') {
                    const tagOptions = normalizeTagOptions(column.options);
                    const labelByValue = new Map(
                      tagOptions.map(option => [option.value, resolveTagLabel(option)])
                    );
                    const tagsArray = Array.isArray(cellValue) ? cellValue : [];
                    const resolvedTags = tagsArray.map(tag => {
                      const tagValue = typeof tag === 'object' && tag !== null
                        ? tag.value || tag.label
                        : tag;
                      return labelByValue.get(tagValue) || tagValue;
                    });

                    if (isEditable && tagOptions.length > 0) {
                      return (
                        <CellTag key={`${block.id}-${column.key}-${rowIndex}`} scope={cellScope}>
                          <div className="unified-module__tag-selector">
                            {tagOptions.map(option => {
                              const isActive = tagsArray.includes(option.value);
                              return (
                                <button
                                  key={`${block.id}-${column.key}-${option.value}-${rowIndex}`}
                                  type="button"
                                  className={`unified-module__tag-toggle ${isActive ? 'unified-module__tag-toggle--active' : ''}`}
                                  onClick={() => handleTagToggle(rowIndex, column.key, option.value)}
                                  aria-pressed={isActive}
                                >
                                  {resolveTagLabel(option)}
                                </button>
                              );
                            })}
                          </div>
                        </CellTag>
                      );
                    }

                    return (
                      <CellTag key={`${block.id}-${column.key}-${rowIndex}`} scope={cellScope}>
                        {resolvedTags.length > 0 ? (
                          <div className="unified-module__tag-list">
                            {resolvedTags.map(tag => (
                              <span key={`${block.id}-${tag}-${rowIndex}`} className="unified-module__tag">
                                {tag}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span>-</span>
                        )}
                      </CellTag>
                    );
                  }

                  if (column.type === 'badge') {
                    const value = typeof cellValue === 'object' && cellValue !== null
                      ? cellValue.value
                      : cellValue;
                    const meetsTarget = typeof cellValue === 'object' && cellValue !== null
                      ? cellValue.meetsTarget
                      : Number(value) >= (column.target || 0);
                    return (
                      <CellTag key={`${block.id}-${column.key}-${rowIndex}`} scope={cellScope}>
                        <div className="unified-module__badge-wrapper">
                          <span className="unified-module__badge-value">{value}</span>
                          {meetsTarget && (
                            <span className="unified-module__badge unified-module__badge--ok">
                              {t('reports.workspace.blocks.coverage.ok')}
                            </span>
                          )}
                        </div>
                      </CellTag>
                    );
                  }

                  if (isEditable) {
                    const value = cellValue ?? '';
                    if (column.multiline) {
                      return (
                        <CellTag key={`${block.id}-${column.key}-${rowIndex}`} scope={cellScope}>
                          <textarea
                            className="unified-module__table-textarea"
                            value={value}
                            onChange={(event) => handleCellChange(rowIndex, column.key, event.target.value)}
                            onBlur={() => handleImmediateSave(block.id)}
                            rows={4}
                          />
                        </CellTag>
                      );
                    }
                    return (
                      <CellTag key={`${block.id}-${column.key}-${rowIndex}`} scope={cellScope}>
                        <input
                          className="unified-module__table-input"
                          value={value}
                          onChange={(event) => handleCellChange(rowIndex, column.key, event.target.value)}
                          onBlur={() => handleImmediateSave(block.id)}
                        />
                      </CellTag>
                    );
                  }

                  return (
                    <CellTag key={`${block.id}-${column.key}-${rowIndex}`} scope={cellScope}>
                      {cellValue}
                    </CellTag>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const renderImageBlock = (block) => {
    const images = block.imageList || [];
    if (!images.length) {
      return (
        <div className="unified-module__empty">
          {t('reports.workspace.blocks.emptyImages')}
        </div>
      );
    }

    if (block.imageLayout === 'grid') {
      return (
        <div className="unified-module__image-grid">
          {images.map((image, index) => (
            <div key={`${block.id}-image-${index}`} className="unified-module__image-card">
              <img src={image.url} alt={image.alt} className="unified-module__image" />
              <div className="unified-module__caption">
                <label className="unified-module__label">
                  {t('reports.workspace.blocks.labels.caption')}
                </label>
                <textarea
                  className="unified-module__textarea"
                  value={image.caption || ''}
                  onChange={(event) => {
                    const nextCaption = event.target.value;
                    updateBlock(block.id, current => {
                      const nextImages = (current.imageList || []).map((item, idx) =>
                        idx === index ? { ...item, caption: nextCaption } : item
                      );
                      return { imageList: nextImages };
                    });
                    scheduleSave(block.id);
                  }}
                  onBlur={() => handleImmediateSave(block.id)}
                  rows={7}
                />
              </div>
            </div>
          ))}
        </div>
      );
    }

    return <ImageCarousel block={block} images={images} onUpdateBlock={updateBlock} onSave={scheduleSave} onBlurSave={handleImmediateSave} />;
  };

  const renderTextArea = (block, labelKey) => {
    const label = getBlockLabel(block, labelKey);
    const placeholder = block.bodyPlaceholderKey
      ? t(block.bodyPlaceholderKey)
      : block.bodyPlaceholder;
    return (
      <div className="unified-module__text">
        <label className="unified-module__label">{label}</label>
        <textarea
          className="unified-module__textarea"
          value={block.bodyText || ''}
          onChange={(event) => {
            updateBlock(block.id, { bodyText: event.target.value });
            scheduleSave(block.id);
          }}
          onBlur={() => handleImmediateSave(block.id)}
          placeholder={placeholder || undefined}
          rows={6}
        />
      </div>
    );
  };

  const resolveSensorName = (section) => {
    if (section.sensorName) {
      return section.sensorName;
    }
    const metadataSensor = section.blocks
      ?.flatMap(block => block.metadata || [])
      .find(item => item.labelKey === 'reports.workspace.blocks.metadata.sensor');
    return metadataSensor?.value || '';
  };

  const resolveSensorUnit = (section, chartBlocks) => {
    if (section.sensorUnit) {
      return section.sensorUnit;
    }
    const chartUnit = chartBlocks.find(block => block.chart?.unit)?.chart?.unit;
    return chartUnit || '';
  };

  const resolveSensorPeriod = (section, chartBlocks) => {
    if (section.sensorPeriod) {
      return section.sensorPeriod;
    }
    const periods = chartBlocks
      .map(block => block.sensorPeriod || getChartPeriodLabel(block.chart))
      .filter(Boolean);
    const uniquePeriods = Array.from(new Set(periods));
    return uniquePeriods.join(' / ');
  };

  const renderChartCaption = (block) => {
    const label = block.captionLabelKey
      ? t(block.captionLabelKey)
      : t('reports.workspace.blocks.labels.caption');
    const placeholder = block.captionPlaceholderKey
      ? t(block.captionPlaceholderKey)
      : block.captionPlaceholder || t('reports.workspace.blocks.placeholders.chartCaption');
    const value = block.caption ?? block.bodyText ?? '';
    return (
      <div className="unified-module__text">
        <label className="unified-module__label">{label}</label>
        <textarea
          className="unified-module__textarea"
          value={value}
          onChange={(event) => {
            const nextValue = event.target.value;
            updateBlock(block.id, { caption: nextValue, bodyText: nextValue });
            scheduleSave(block.id);
          }}
          onBlur={() => handleImmediateSave(block.id)}
          placeholder={placeholder || undefined}
          rows={7}
        />
      </div>
    );
  };

  const renderBlockActions = (block) => {
    const isApproved = block.status === 'approved';
    return (
      <div className="unified-module__actions">
        <Button
          variant={isApproved ? 'secondary' : 'primary'}
          size="sm"
          onClick={() => handleApproveToggle(block)}
        >
          {isApproved
            ? t('reports.workspace.blocks.approved')
            : t('reports.workspace.blocks.approve')}
        </Button>

        <div className="unified-module__comment">
          <label className="unified-module__label">
            {t('reports.workspace.blocks.commentLabel')}
          </label>
          <textarea
            className="unified-module__textarea"
            value={block.commentDraft || ''}
            onChange={(event) =>
              updateBlock(block.id, { commentDraft: event.target.value })
            }
            placeholder={t('reports.workspace.blocks.commentPlaceholder')}
            rows={6}
          />
          <div className="unified-module__comment-actions">
            <Button
              variant="ghost"
              size="sm"
              icon={<MessageSquare size={14} />}
              onClick={() => handleCommentSubmit(block)}
              disabled={!block.commentDraft?.trim()}
            >
              {t('reports.workspace.blocks.commentSubmit')}
            </Button>
          </div>
        </div>
      </div>
    );
  };

  const renderCommentHistory = (block) => {
    if (!Array.isArray(block.commentHistory) || block.commentHistory.length === 0) {
      return null;
    }
    return (
      <div className="unified-module__comment-history">
        <div className="unified-module__comment-history-title">
          {t('reports.workspace.blocks.commentHistoryTitle')}
        </div>
        {block.commentHistory.map(comment => (
          <div key={comment.id} className="unified-module__comment-item">
            <div className="unified-module__comment-text">{comment.text}</div>
            <div className="unified-module__comment-meta">
              <span>{formatTimestamp(comment.createdAt)}</span>
              <span>{t('reports.workspace.blocks.commentStatusSubmitted')}</span>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderAudit = (block) => {
    const lastEdited = formatTimestamp(block.lastEditedAt);
    const approvedAt = formatTimestamp(block.approvedAt);
    if (!lastEdited && !approvedAt) {
      return null;
    }
    return (
      <div className="unified-module__audit">
        {lastEdited && (
          <span>
            {t('reports.workspace.blocks.lastEdited', {
              name: block.lastEditedBy || t('reports.workspace.fallback.user'),
              date: lastEdited,
            })}
          </span>
        )}
        {approvedAt && (
          <span>
            {t('reports.workspace.blocks.approvedBy', {
              name: block.approvedBy || t('reports.workspace.fallback.user'),
              date: approvedAt,
            })}
          </span>
        )}
      </div>
    );
  };

  const renderBlockCard = ({ block, body, titleOverride, showMetadata = true, className = '' }) => {
    const isApproved = block.status === 'approved';
    const saveStatus = saveState[block.id];
    const title = resolveBlockTitle(block, titleOverride);
    const blockClasses = ['unified-module__block', className].filter(Boolean).join(' ');
    return (
      <section key={block.id} className={blockClasses}>
        <div className="unified-module__block-header">
          <div>
            {title ? <h3 className="unified-module__block-title">{title}</h3> : null}
            {showMetadata && renderMetadata(block)}
            {saveStatus && (
              <div className="unified-module__saving">
                {saveStatus === 'saving'
                  ? t('reports.workspace.blocks.saving')
                  : t('reports.workspace.blocks.saved')}
              </div>
            )}
          </div>
          <span
            className={`unified-module__block-status ${isApproved ? 'unified-module__block-status--approved' : 'unified-module__block-status--draft'}`}
          >
            {isApproved
              ? t('reports.workspace.blocks.statusApproved')
              : t('reports.workspace.blocks.statusDraft')}
          </span>
        </div>

        <div className="unified-module__block-body">
          {body}
        </div>

        {renderBlockActions(block)}
        {renderCommentHistory(block)}
        {renderAudit(block)}
      </section>
    );
  };

  const renderStandardBlock = (block) => {
    const body = (
      <>
        {block.type === 'chart' && renderChart(block)}
        {block.type === 'table' && renderTable(block)}
        {block.type === 'image' && renderImageBlock(block)}
        {block.type === 'text' && renderTextArea(block, 'reports.workspace.blocks.labels.narrative')}
        {(block.type === 'chart' || block.type === 'table') &&
          renderTextArea(block, 'reports.workspace.blocks.labels.interpretation')}
      </>
    );
    return renderBlockCard({ block, body, showMetadata: true });
  };

  const renderSensorSection = (section) => {
    const chartBlocks = section.blocks.filter(block => block.type === 'chart');
    const tableBlocks = section.blocks.filter(block => block.type === 'table');
    const imageBlocks = section.blocks.filter(block => block.type === 'image');
    const interpretationBlock = section.blocks.find(block => block.blockRole === 'interpretation')
      || section.blocks.find(block => block.type === 'text');
    const retroBlock = imageBlocks.find(block => block.imageGroup === 'retro_modulo') || imageBlocks[0];
    const campoBlock = imageBlocks.find(block => block.imageGroup === 'campo_aperto') || imageBlocks[1];
    const sensorName = resolveSensorName(section)
      || t('reports.workspace.blocks.metadata.sensor', { defaultValue: 'Sensor' });
    const sensorUnit = resolveSensorUnit(section, chartBlocks)
      || t('common.notAvailable', { defaultValue: 'N/A' });
    const sensorPeriod = resolveSensorPeriod(section, chartBlocks)
      || t('common.notAvailable', { defaultValue: 'N/A' });
    const interpretationTitle = interpretationBlock?.title
      || t('reports.workspace.blocks.labels.interpretation');

    return (
      <section key={section.id} className="unified-module__sensor-section">
        <div className="unified-module__sensor-header">
          <h3 className="unified-module__sensor-title">{sensorName}</h3>
          <div className="unified-module__sensor-meta">
            <span className="unified-module__sensor-meta-item">
              <span className="unified-module__sensor-meta-label">
                {t('reports.workspace.blocks.metadata.unit')}
              </span>
              <span>{sensorUnit}</span>
            </span>
            <span className="unified-module__sensor-meta-item">
              <span className="unified-module__sensor-meta-label">
                {t('reports.workspace.blocks.metadata.period')}
              </span>
              <span>{sensorPeriod}</span>
            </span>
          </div>
        </div>

        <div className="unified-module__sensor-content">
          {chartBlocks.length > 0 && (
            <div className="unified-module__sensor-charts">
              {chartBlocks.map(block =>
                renderBlockCard({
                  block,
                  body: (
                    <>
                      {renderChart(block)}
                      {renderChartCaption(block)}
                    </>
                  ),
                  showMetadata: false,
                  className: 'unified-module__block--nested',
                })
              )}
            </div>
          )}

          {tableBlocks.length > 0 && (
            <div className="unified-module__sensor-tables">
              {tableBlocks.map(block =>
                renderBlockCard({
                  block,
                  body: (
                    <>
                      {renderTable(block)}
                      {renderTextArea(block, 'reports.workspace.blocks.labels.caption')}
                    </>
                  ),
                  showMetadata: false,
                  className: 'unified-module__block--nested',
                })
              )}
            </div>
          )}

          {(retroBlock || campoBlock) && (
            <div className="unified-module__sensor-images">
              {retroBlock && renderBlockCard({
                block: retroBlock,
                body: renderImageBlock(retroBlock),
                showMetadata: false,
                className: 'unified-module__block--nested unified-module__block--image',
              })}
              {campoBlock && renderBlockCard({
                block: campoBlock,
                body: renderImageBlock(campoBlock),
                showMetadata: false,
                className: 'unified-module__block--nested unified-module__block--image',
              })}
            </div>
          )}

          {interpretationBlock && (
            <div className="unified-module__sensor-interpretation">
              {renderBlockCard({
                block: interpretationBlock,
                body: renderTextArea(interpretationBlock, 'reports.workspace.blocks.labels.interpretation'),
                titleOverride: interpretationTitle,
                showMetadata: false,
                className: 'unified-module__block--nested',
              })}
            </div>
          )}
        </div>
      </section>
    );
  };

  if (!blocks.length) {
    return (
      <div className="unified-module__empty">
        {t('reports.workspace.blocks.emptyState')}
      </div>
    );
  }

  return (
    <div className="unified-module">
      {standaloneBlocks.map(block => renderStandardBlock(block))}
      {sensorSections.map(section => renderSensorSection(section))}
    </div>
  );
}

function ImageCarousel({ block, images, onUpdateBlock, onSave, onBlurSave }) {
  const { t } = useTranslation();
  const [index, setIndex] = useState(0);
  const total = images.length;
  const currentImage = images[index] || images[0];

  const handlePrev = () => {
    setIndex(prev => (prev - 1 + total) % total);
  };

  const handleNext = () => {
    setIndex(prev => (prev + 1) % total);
  };

  if (!currentImage) {
    return (
      <div className="unified-module__empty">
        {t('reports.workspace.blocks.emptyImages')}
      </div>
    );
  }

  return (
    <div className="unified-module__carousel">
      <img src={currentImage.url} alt={currentImage.alt} className="unified-module__image" />
      {total > 1 && (
        <div className="unified-module__carousel-controls">
          <button type="button" className="unified-module__carousel-button" onClick={handlePrev}>
            <ChevronLeft size={16} />
          </button>
          <button type="button" className="unified-module__carousel-button" onClick={handleNext}>
            <ChevronRight size={16} />
          </button>
        </div>
      )}
      {total > 1 && (
        <div className="unified-module__carousel-count">
          {index + 1} / {total}
        </div>
      )}
      <div className="unified-module__caption">
        <label className="unified-module__label">
          {t('reports.workspace.blocks.labels.caption')}
        </label>
        <textarea
          className="unified-module__textarea"
          value={currentImage.caption || ''}
          onChange={(event) => {
            const nextCaption = event.target.value;
            onUpdateBlock(block.id, current => {
              const nextImages = (current.imageList || []).map((item, idx) =>
                idx === index ? { ...item, caption: nextCaption } : item
              );
              return { imageList: nextImages };
            });
            onSave(block.id);
          }}
          onBlur={() => onBlurSave(block.id)}
          rows={7}
        />
      </div>
    </div>
  );
}
